import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, ArrowRight, Phone } from "lucide-react";
import { RESOURCE_BY_SLUG, RESOURCES_CATEGORIES, RESOURCES } from "@/lib/resources";

export default function ResourceArticlePage() {
  const { slug } = useParams();
  const article = RESOURCE_BY_SLUG[slug];
  if (!article) return <Navigate to="/ressources" replace />;
  const category = RESOURCES_CATEGORIES.find((c) => c.slug === article.category);
  const related = RESOURCES.filter((r) => r.category === article.category && r.slug !== article.slug).slice(0, 3);

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-white">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/ressources" className="inline-flex items-center gap-2 text-sm font-semibold mb-8 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Toutes les ressources
        </Link>

        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">
          {category?.emoji} {category?.label}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">{article.title}</h1>
        <div className="flex items-center gap-3 mt-5 text-xs text-neutral-500 uppercase tracking-[0.15em] font-bold">
          <Clock className="w-3 h-3" /> {article.readTime} de lecture
        </div>

        <div className="aspect-[16/9] overflow-hidden my-10 -mx-4 sm:-mx-0">
          <img src={article.image} alt="" className="w-full h-full object-cover" />
        </div>

        <p className="text-lg text-neutral-700 leading-relaxed font-light mb-10">{article.excerpt}</p>

        <div className="prose prose-neutral max-w-none">
          {article.sections.map((s, i) => (
            <section key={i} className="border-t border-black/10 pt-8 mt-8 first:border-0 first:pt-0 first:mt-0">
              <h2 className="font-display text-2xl font-bold tracking-tight">{s.h}</h2>
              <p className="text-neutral-700 leading-relaxed mt-4">{s.p}</p>
            </section>
          ))}
        </div>

        {/* Support callout */}
        <div className="mt-16 border-2 border-[#0A0A0A] p-7 flex items-start gap-4">
          <Phone className="w-6 h-6 mt-1 shrink-0" />
          <div>
            <div className="font-display text-xl font-bold">Une question spécifique à votre situation ?</div>
            <p className="text-sm text-neutral-700 mt-2">Appelez notre équipe à Beaussais-Sur-Mer : <a href="tel:+33972158090" className="font-bold underline">09 72 15 80 90</a> · 8h–23h tous les jours.</p>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#F5F2EC] py-16 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-600 font-bold mb-3">À lire ensuite</div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Dans la même catégorie</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link to={`/ressources/${r.slug}`} key={r.slug} className="group block bg-white border border-black/10 hover:border-[#0A0A0A] transition-colors">
                  <div className="aspect-[3/2] overflow-hidden">
                    <img src={r.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <div className="font-display text-lg font-bold leading-tight">{r.title}</div>
                    <div className="flex items-center gap-2 mt-3 text-xs text-neutral-500"><Clock className="w-3 h-3" /> {r.readTime}</div>
                    <div className="text-xs mt-3 inline-flex items-center gap-1 font-semibold">Lire <ArrowRight className="w-3 h-3" /></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
